import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Switch } from 'antd';
import { TbCircle, TbMinusVertical, TbNote } from 'react-icons/tb';

import { useEntitlements } from '@/hooks/useEntitlements';
import { workspaceWhitelabelQueryKey, workspaceWhitelabelQueryOptions } from '@/queries';
import { workspaceService } from '@/services/database';
import { FullHeightFlexColumn, LearnMoreFooter, PlatformAlert } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useWorkspaceStore } from '@/stores';
import type { Whitelabel } from '@/types';

import AdvancedSettingsSectionHeader from '../components/AdvancedSettingsSectionHeader';
import AdvancedSettingsViewHeader from '../components/AdvancedSettingsViewHeader';
import Whitelabeling from './01-Whitelabeling';
import WhitelabelingSkeleton from './components/WhitelabelingSkeleton';

// interface AppearanceProps {}

const Appearance: FC = (): JSX.Element => {
    const [enabledOverride, setEnabledOverride] = useState<{ enabled: boolean; workspaceId: string } | null>(null);
    const [whitelabelingSaving, setWhitelabelingSaving] = useState(false);

    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { hasFeature } = useEntitlements();
    const canUseWhitelabeling = hasFeature('whitelabeling');
    const queryClient = useQueryClient();
    const whitelabelQuery = useQuery(workspaceWhitelabelQueryOptions(workspace?.id));
    const whitelabelingEnabled =
        enabledOverride && enabledOverride.workspaceId === workspace?.id
            ? enabledOverride.enabled
            : Boolean(whitelabelQuery.data?.enabled);

    useEffect(() => {
        if (whitelabelQuery.error) {
            captureException(whitelabelQuery.error);
        }
    }, [whitelabelQuery.error]);

    const cacheWhitelabel = (nextWhitelabel: Whitelabel) => {
        if (!workspace?.id) {
            return;
        }

        queryClient.setQueryData(workspaceWhitelabelQueryKey(workspace.id), nextWhitelabel);
        setEnabledOverride(null);
    };

    const handleWhitelabelingToggle = async (checked: boolean) => {
        if (!workspace?.id) {
            return;
        }

        setEnabledOverride({ enabled: checked, workspaceId: workspace.id });

        if (!checked) {
            return;
        }

        setWhitelabelingSaving(true);

        try {
            const result = await workspaceService.updateWhitelabel(workspace.id, { enabled: true });

            cacheWhitelabel(result);
        } catch (error) {
            captureException(error);
            setEnabledOverride({ enabled: false, workspaceId: workspace.id });
            toast.error('Whitelabeling could not be enabled.');
        } finally {
            setWhitelabelingSaving(false);
        }
    };

    return (
        <FullHeightFlexColumn>
            <AdvancedSettingsViewHeader
                description='Manage settings for your company appearance here.'
                title='Appearance'
            />
            <AdvancedSettingsSectionHeader
                description="Apply your company's branding to your workspace & publicly shared resources."
                icon={<TbNote size='1rem' />}
                plan='enterprise'
                title='Whitelabeling'
                extra={
                    <Switch
                        disabled={
                            whitelabelingEnabled ||
                            !canUseWhitelabeling ||
                            whitelabelingSaving ||
                            whitelabelQuery.isLoading
                        }
                        loading={whitelabelQuery.isLoading || whitelabelingSaving}
                        size='default'
                        style={{ verticalAlign: '-1rem' }}
                        checked={whitelabelingEnabled}
                        checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.1rem' }} />}
                        unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.1rem' }} />}
                        onChange={handleWhitelabelingToggle}
                    />
                }
            />
            {whitelabelQuery.isLoading ? <WhitelabelingSkeleton /> : null}
            {whitelabelQuery.isError ? (
                <PlatformAlert
                    action={
                        <Button size='small' onClick={() => void whitelabelQuery.refetch()}>
                            Try again
                        </Button>
                    }
                    description='The saved workspace appearance could not be loaded. Your existing theme has not been changed.'
                    status='error'
                    styles={{ marginTop: '1rem' }}
                    title='Appearance settings unavailable'
                />
            ) : null}
            {!whitelabelQuery.isLoading && !whitelabelQuery.isError && whitelabelingEnabled ? (
                <Whitelabeling
                    enabled={whitelabelingEnabled}
                    initialWhitelabel={whitelabelQuery.data}
                    onSaved={cacheWhitelabel}
                    setEnabled={(enabled) => {
                        if (workspace?.id) {
                            setEnabledOverride({ enabled, workspaceId: workspace.id });
                        }
                    }}
                />
            ) : null}
            <footer
                aria-label='Appearance preferences documentation'
                data-whitelabeling-documentation-footer='true'
                style={{ flex: 'none', marginTop: 'auto', paddingBottom: '1rem' }}
            >
                <Divider style={{ margin: '1rem 0' }} />
                <LearnMoreFooter align='center' topic={<>appearance preferences</>} />
            </footer>
        </FullHeightFlexColumn>
    );
};

export default Appearance;
